/**
 * sl-bpu6.4: ConfigProvisionerService — atomically provision a Custom App.
 *
 * Orchestrates ConfigDatabase + standard Custom App database creation inside a
 * single BlockSuite doc and seeds an initial ViewConfigRow. Two paths:
 *
 *  1. New Custom App — provisioner creates ConfigDB plus pages/navigation/clusters/
 *                topics/content/translations/forms/submissions databases.
 *  2. Adoption — caller passes `existingContentDbBlockId`; provisioner links
 *                that block as ContentDB and creates the remaining standard
 *                databases. The adopted block must not already carry an
 *                `sl_view_role` marker (fail-loud).
 *
 * Rollback: on any failure after the ConfigDB is created, the provisioner
 * deletes every block it inserted via `doc.deleteBlock` so the doc is left
 * in its pre-provision state.
 */
import {
  GraphQLService,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { ensureCustomAppDataSourcesMutation } from '@affine/graphql';
import { isDatabaseStorageModelReady } from '@blocksuite/affine/blocks/database';
import {
  createDatabaseBlockDataSource,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine-block-database';
import {
  assertDatabaseIdentity,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import type { Store } from '@blocksuite/store';
import {
  createServerDatabaseRootDocId,
  isValidDatabaseBlockId,
  normalizeDatabaseBlockId,
} from '@streamlain/database-identity';
import {
  buildProtectedCustomAppStandardColumnSpecs,
  classifyDatabaseRole,
  createCustomAppDataSourceKey,
  createDefaultDataSourceRef,
  createEmptyViewConfigRow,
  CUSTOM_APP_STANDARD_DATABASE_LAYOUTS,
  type CustomAppConfig,
  CustomAppConfigSchema,
  type CustomAppContentModel,
  CustomAppDatabaseBindingSchema,
  type CustomAppDataSourceRole,
  type DataSourceRegistry,
  getDefaultCustomAppContentModelForRole,
  getInitialCustomAppContentModelForRole,
  INITIAL_CUSTOM_APP_DATABASE_ROLES,
  serializeCustomAppDatabaseXYWH,
  SL_VIEW_ROLE_BY_STANDARD_ROLE,
  SL_VIEW_ROLE_KEY,
  SL_VIEW_ROLE_VALUES,
  STANDARD_CUSTOM_APP_DATABASE_ROLES,
  STANDARD_CUSTOM_APP_DATABASE_TITLES,
  type StandardDatabaseRole,
  type ViewConfigRow,
} from '@streamlain/view-config';
import { Framework, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { Text as YText } from 'yjs';

import type { DocService, DocsService } from '../../doc';
import { ViewConfigRowStore } from '../stores/view-config-row.store';
import {
  ensureStandardRelationColumns,
  getCustomAppDataSources,
} from './custom-app-standard-database-schema';
import { isDatabaseSnapshotFullyLoaded } from './database-snapshot-readiness';
import { ViewConfigDatabaseService } from './view-config-database.service';

type ProvisionedDatabaseRole = Exclude<StandardDatabaseRole, 'config'>;

type StandardDatabaseBlockIdMap = Partial<
  Record<ProvisionedDatabaseRole, string>
>;

const STANDARD_DATABASE_READY_TIMEOUT_MS = 5000;
const STANDARD_DATABASE_READY_POLL_MS = 50;

function isServerOwnedDataSourceAccessNotReady(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current; depth += 1) {
    if (
      typeof current === 'object' &&
      current !== null &&
      'failureClass' in current &&
      current.failureClass === 'permission_lag'
    ) {
      return true;
    }
    if (
      current instanceof Error &&
      (current.message.includes('ACCESS_DENIED') ||
        current.message.includes('permission_lag'))
    ) {
      return true;
    }
    current =
      typeof current === 'object' && current !== null && 'cause' in current
        ? current.cause
        : null;
  }
  return false;
}

function isCloudDocStorageConnectionNotReady(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current; depth += 1) {
    if (
      current instanceof Error &&
      current.message.includes(
        'CloudDocStorageConnection has not been established'
      )
    ) {
      return true;
    }
    current =
      typeof current === 'object' && current !== null && 'cause' in current
        ? current.cause
        : null;
  }
  return false;
}

function buildStandardColumns(
  role: ProvisionedDatabaseRole,
  contentModel?: CustomAppContentModel
): Array<{
  id: string;
  type: string;
  name: string;
  data: Record<string, unknown>;
}> {
  return buildProtectedCustomAppStandardColumnSpecs({
    role,
    contentModel,
    createId: () => nanoid(),
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function normalizeProvisionedRoutePath(path: string): string {
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withLeadingSlash.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function hasPublishedRootRoute(row: ViewConfigRow): boolean {
  const pages = row.customAppConfig?.pages;
  if (!Array.isArray(pages)) return false;
  return pages.some(
    page =>
      page.status === 'published' &&
      normalizeProvisionedRoutePath(page.path) === '/'
  );
}

function normalizeProvisionedDatabaseBlockId(
  value: string | undefined,
  context: string
): string | undefined {
  if (value === undefined) return undefined;
  const normalized = normalizeDatabaseBlockId(value);
  if (!isValidDatabaseBlockId(normalized)) {
    throw new Error(
      `ConfigProvisioner: ${context} must be a database block id, received "${value}"`
    );
  }
  return normalized;
}

function normalizeStandardDatabaseBlockIds(
  blockIds: StandardDatabaseBlockIdMap | undefined
): StandardDatabaseBlockIdMap | undefined {
  if (!blockIds) return undefined;
  const normalized: StandardDatabaseBlockIdMap = {};
  for (const [role, value] of Object.entries(blockIds) as Array<
    [ProvisionedDatabaseRole, string | undefined]
  >) {
    const blockId = normalizeProvisionedDatabaseBlockId(
      value,
      `standard database ${role}`
    );
    if (blockId) normalized[role] = blockId;
  }
  return normalized;
}

function normalizeCustomAppConfigDataSourceRefs(
  customAppConfig: CustomAppConfig
): CustomAppConfig {
  let bindingsChanged = false;
  const databaseBindings = Object.fromEntries(
    Object.entries(customAppConfig.databaseBindings).map(
      ([sourceKey, binding]) => {
        const databaseId = normalizeProvisionedDatabaseBlockId(
          binding.databaseId,
          `database binding ${sourceKey} databaseId`
        )!;
        const blockId = normalizeProvisionedDatabaseBlockId(
          binding.placement.blockId,
          `database binding ${sourceKey} blockId`
        )!;
        const originalBlockId = normalizeProvisionedDatabaseBlockId(
          binding.originalPlacement.blockId,
          `database binding ${sourceKey} original blockId`
        )!;
        if (
          databaseId === binding.databaseId &&
          blockId === binding.placement.blockId &&
          originalBlockId === binding.originalPlacement.blockId
        ) {
          return [sourceKey, binding] as const;
        }
        bindingsChanged = true;
        return [
          sourceKey,
          CustomAppDatabaseBindingSchema.parse({
            ...binding,
            databaseId,
            placement: {
              ...binding.placement,
              blockId,
            },
            originalPlacement: {
              ...binding.originalPlacement,
              blockId: originalBlockId,
            },
          }),
        ] as const;
      }
    )
  );
  if (!bindingsChanged) return customAppConfig;
  return CustomAppConfigSchema.parse({
    ...customAppConfig,
    databaseBindings,
  });
}

export interface ProvisionInput {
  docId: string;
  /** Display name of the initial (default) ViewConfigRow. */
  title: string;
  /**
   * Pass the block-id of a user-selected DatabaseBlock in the SAME doc to
   * adopt it as the ContentDB. When omitted, the provisioner creates a fresh
   * Title-only ContentDB.
   */
  existingContentDbBlockId?: string;
  /**
   * Backend-provisioned ids for local-first hydration. When remote sync has
   * not reached this session yet, the frontend mirrors the same identities.
   */
  fixedIds?: {
    configDbBlockId: string;
    contentDbBlockId?: string;
    viewConfigRowId: string;
    standardDatabaseBlockIds?: StandardDatabaseBlockIdMap;
  };
}

export interface ProvisionResult {
  /** Block id of the newly-created (or pre-existing) ConfigDB. */
  configDbBlockId: string;
  /** Block id of the ContentDB (new or adopted). */
  contentDbBlockId: string;
  /** Block ids for the vNext standard databases provisioned for this App. */
  standardDatabaseBlockIds: StandardDatabaseBlockIdMap;
  /** Block id of the initial ViewConfigRow inside the ConfigDB. */
  viewConfigRowId: string;
}

export class ConfigProvisionerService extends Service {
  private readonly ensureStandardDatabasesInFlight = new Map<
    string,
    Promise<CustomAppConfig>
  >();

  constructor(
    private readonly docsService: DocsService,
    private readonly viewConfigDatabase: ViewConfigDatabaseService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  async provision(input: ProvisionInput): Promise<ProvisionResult> {
    const fixedIds = input.fixedIds
      ? {
          configDbBlockId: normalizeProvisionedDatabaseBlockId(
            input.fixedIds.configDbBlockId,
            'config database id'
          )!,
          contentDbBlockId: normalizeProvisionedDatabaseBlockId(
            input.fixedIds.contentDbBlockId,
            'content database id'
          ),
          standardDatabaseBlockIds: normalizeStandardDatabaseBlockIds(
            input.fixedIds.standardDatabaseBlockIds
          ),
          viewConfigRowId: input.fixedIds.viewConfigRowId,
        }
      : undefined;
    const { doc, release } = this.docsService.open(input.docId);
    try {
      if (fixedIds) {
        // The backend has already provisioned the host block graph and all
        // database root/shard docs. Pull that authoritative host snapshot
        // before inspecting or mirroring fixed identities; otherwise the
        // local-first shell can manufacture empty database roots and push
        // them over the server-owned manifests during normal sync startup.
        await doc.workspace.engine.doc.pullRemote(input.docId);
      }
      const bsDoc = doc.blockSuiteDoc as Store | undefined;
      // A remote pull reconciles worker storage, not the opened foreground
      // store. Inspect the block graph only after its existing load barrier.
      if (bsDoc) {
        bsDoc.load?.();
        await doc.waitForSyncReady();
      }
      if (!bsDoc?.root) {
        throw new Error(
          `ConfigProvisioner: doc ${input.docId} is not ready (no root block)`
        );
      }
      // One Custom App (= one ConfigDB + one ContentDB) per doc.
      // findDatabaseBlockId matches on either the `managedBy` tag or the
      // `sl_view_role` marker.
      const existingConfigDb =
        this.viewConfigDatabase.findDatabaseBlockId(bsDoc);
      if (existingConfigDb) {
        if (
          fixedIds?.configDbBlockId === existingConfigDb &&
          fixedIds.standardDatabaseBlockIds
        ) {
          const createdBlockIds: string[] = [];
          try {
            const standardDatabaseBlockIds =
              await this.ensureStandardDatabaseBlocks(
                bsDoc,
                input.docId,
                fixedIds.standardDatabaseBlockIds,
                createdBlockIds
              );
            const contentDbBlockId =
              standardDatabaseBlockIds.content ??
              fixedIds.contentDbBlockId ??
              '';
            this.ensureFixedViewConfigRow({
              configDbBlockId: existingConfigDb,
              contentDbBlockId,
              dataSources: this.buildDataSourceRegistry(
                input.docId,
                standardDatabaseBlockIds
              ),
              doc,
              docId: input.docId,
              title: input.title,
              viewConfigRowId: fixedIds.viewConfigRowId,
            });
            return {
              configDbBlockId: existingConfigDb,
              contentDbBlockId,
              standardDatabaseBlockIds,
              viewConfigRowId: fixedIds.viewConfigRowId,
            };
          } catch (err) {
            for (const id of [...createdBlockIds].reverse()) {
              try {
                const b = bsDoc.getBlock(id);
                if (b?.model) bsDoc.deleteBlock(b.model);
              } catch {
                // best-effort cleanup; keep original provisioning error
              }
            }
            throw err;
          }
        }
        throw new Error(
          `ConfigProvisioner: doc ${input.docId} already has a ConfigDB (${existingConfigDb}); only one Custom App per doc is supported`
        );
      }

      // Rollback state — populated as we mutate the doc. On error we undo
      // in reverse: delete blocks we created, restore marks we overwrote.
      // Without adoptedMarks the adopted ContentDB would keep the
      // `sl_view_role` mark even after rollback, which locks the user's
      // DatabaseBlock out of future adoption (classifyDatabaseRole → 'content').
      const createdBlockIds: string[] = [];
      const adoptedMarks: Array<{
        blockId: string;
        previousMark: string | null;
      }> = [];
      try {
        const { databaseBlockId: configDbBlockId } =
          this.viewConfigDatabase.provision(bsDoc, fixedIds?.configDbBlockId);
        createdBlockIds.push(configDbBlockId);

        const contentDbBlockId = input.existingContentDbBlockId
          ? this.adoptContentDb(
              bsDoc,
              input.existingContentDbBlockId,
              adoptedMarks
            )
          : this.createStandardDb(
              bsDoc,
              'content',
              createdBlockIds,
              fixedIds?.contentDbBlockId ??
                fixedIds?.standardDatabaseBlockIds?.content,
              getInitialCustomAppContentModelForRole('content')
            );

        const standardDatabaseBlockIds: StandardDatabaseBlockIdMap = {
          content: contentDbBlockId,
        };
        for (const role of INITIAL_CUSTOM_APP_DATABASE_ROLES) {
          if (role === 'content') continue;
          standardDatabaseBlockIds[role] = this.createStandardDb(
            bsDoc,
            role,
            createdBlockIds,
            fixedIds?.standardDatabaseBlockIds?.[role],
            getInitialCustomAppContentModelForRole(role)
          );
        }

        const contentModelsByRole = Object.fromEntries(
          Object.keys(standardDatabaseBlockIds).map(role => [
            role,
            getInitialCustomAppContentModelForRole(
              role as ProvisionedDatabaseRole
            ),
          ])
        ) as Partial<Record<ProvisionedDatabaseRole, CustomAppContentModel>>;

        const dataSources = this.buildDataSourceRegistry(
          input.docId,
          standardDatabaseBlockIds,
          contentModelsByRole
        );
        this.ensureProvisionedRelationColumns(bsDoc, dataSources);

        const rowStore = this.buildRowStore(
          { doc } as unknown as DocService,
          configDbBlockId
        );
        const viewConfigRowId = rowStore.createRow(
          createEmptyViewConfigRow({
            name: input.title,
            contentDbRef: {
              databaseId: contentDbBlockId,
              docId: input.docId,
              blockId: contentDbBlockId,
              contentModel: getInitialCustomAppContentModelForRole('content'),
            },
            dataSources,
            isDefault: true,
          }),
          fixedIds?.viewConfigRowId
        );

        return {
          configDbBlockId,
          contentDbBlockId,
          standardDatabaseBlockIds,
          viewConfigRowId,
        };
      } catch (err) {
        for (const id of [...createdBlockIds].reverse()) {
          try {
            const b = bsDoc.getBlock(id);
            if (b?.model) bsDoc.deleteBlock(b.model);
          } catch {
            // best-effort cleanup — swallow errors here so the original
            // failure is the one the caller sees
          }
        }
        for (const { blockId, previousMark } of [...adoptedMarks].reverse()) {
          try {
            const b = bsDoc.getBlock(blockId);
            const props = b?.model?.props as
              | Record<string, unknown>
              | undefined;
            if (!props) continue;
            bsDoc.transact(() => {
              if (previousMark === null) {
                delete props[SL_VIEW_ROLE_KEY];
              } else {
                props[SL_VIEW_ROLE_KEY] = previousMark;
              }
            });
          } catch {
            // best-effort — same reason as above
          }
        }
        throw err;
      }
    } finally {
      release();
    }
  }

  async ensureStandardDatabase(input: {
    workspaceId?: string;
    customAppId?: string;
    docId: string;
    customAppConfig: CustomAppConfig;
    role: ProvisionedDatabaseRole;
    sourceKey?: string;
    contentModel?: CustomAppContentModel;
    label?: string;
  }): Promise<CustomAppConfig> {
    return this.ensureStandardDatabases({
      workspaceId: input.workspaceId,
      customAppId: input.customAppId,
      docId: input.docId,
      customAppConfig: input.customAppConfig,
      requests: [
        {
          role: input.role,
          ...(input.sourceKey ? { sourceKey: input.sourceKey } : {}),
          ...(input.contentModel ? { contentModel: input.contentModel } : {}),
          label: input.label,
        },
      ],
    });
  }

  async ensureStandardDatabases(input: {
    reconcileInstalledSchemas?: boolean;
    workspaceId?: string;
    customAppId?: string;
    docId: string;
    customAppConfig: CustomAppConfig;
    requests: Array<{
      role: ProvisionedDatabaseRole;
      sourceKey?: string;
      contentModel?: CustomAppContentModel;
      label?: string;
    }>;
  }): Promise<CustomAppConfig> {
    void input.docId;
    if (!input.workspaceId || !input.customAppId) {
      throw new Error(
        'ConfigProvisioner: workspaceId and customAppId are required for server-owned data source provisioning'
      );
    }
    const inputCustomAppConfig = normalizeCustomAppConfigDataSourceRefs(
      input.customAppConfig
    );
    const inputDataSources = getCustomAppDataSources(inputCustomAppConfig);
    const workspaceId = input.workspaceId;
    const customAppId = input.customAppId;
    const requests = input.requests.map(request => {
      const existingRef = request.sourceKey
        ? inputDataSources[request.sourceKey]
        : (Object.values(inputDataSources).find(
            ref => ref.role === request.role
          ) ?? null);
      const contentModel: CustomAppContentModel =
        request.contentModel ??
        existingRef?.contentModel ??
        getDefaultCustomAppContentModelForRole(request.role);
      return {
        role: request.role,
        sourceKey:
          request.sourceKey ??
          existingRef?.key ??
          createCustomAppDataSourceKey({ role: request.role, contentModel }),
        contentModel,
        ...(request.label ? { label: request.label } : {}),
      };
    });
    for (const request of requests) {
      const existingRef = inputDataSources[request.sourceKey];
      if (
        existingRef &&
        (existingRef.role !== request.role ||
          existingRef.contentModel !== request.contentModel)
      ) {
        throw new Error(
          `ConfigProvisioner: data source ${request.sourceKey} is ${existingRef.role}/${existingRef.contentModel}, expected ${request.role}/${request.contentModel}`
        );
      }
    }

    const missingRequests = requests.filter(
      request => !inputDataSources[request.sourceKey]
    );
    const ensureThroughServer = async (
      serverRequests: typeof requests
    ): Promise<CustomAppConfig> => {
      const ensureKey = this.createStandardDatabasesEnsureKey({
        customAppId,
        requests: serverRequests,
        workspaceId,
      });
      const inFlight = this.ensureStandardDatabasesInFlight.get(ensureKey);
      if (inFlight) return inFlight;

      const promise = (async () => {
        const result = await this.requireGraphQL().gql({
          query: ensureCustomAppDataSourcesMutation,
          variables: {
            workspaceId,
            id: customAppId,
            input: {
              requests: serverRequests,
            },
          },
        });
        const customAppConfig = normalizeCustomAppConfigDataSourceRefs(
          CustomAppConfigSchema.parse(result.ensureCustomAppDataSources)
        );
        this.assertServerOwnedDataSourcesPresent(
          customAppConfig,
          serverRequests
        );
        await this.waitForDataSourcesReady(
          customAppConfig,
          serverRequests.map(request => request.sourceKey),
          workspaceId,
          { retryPermissionLag: true }
        );
        return customAppConfig;
      })();
      this.ensureStandardDatabasesInFlight.set(ensureKey, promise);
      try {
        return await promise;
      } finally {
        if (this.ensureStandardDatabasesInFlight.get(ensureKey) === promise) {
          this.ensureStandardDatabasesInFlight.delete(ensureKey);
        }
      }
    };
    if (input.reconcileInstalledSchemas) return ensureThroughServer(requests);
    if (missingRequests.length === 0) {
      try {
        await this.waitForDataSourcesReady(
          inputCustomAppConfig,
          requests.map(request => request.sourceKey),
          workspaceId
        );
        return inputCustomAppConfig;
      } catch (error) {
        if (!isServerOwnedDataSourceAccessNotReady(error)) throw error;
        // A second local-first client can observe the new data-source refs
        // before the creating client's server-owned mutation group commits.
        // Re-enter the serialized, idempotent server ensure contract once;
        // its receipt is the authoritative access/readiness barrier.
        return ensureThroughServer(requests);
      }
    }
    return ensureThroughServer(missingRequests);
  }

  private createStandardDatabasesEnsureKey(input: {
    workspaceId: string;
    customAppId: string;
    requests: ReadonlyArray<{
      role: ProvisionedDatabaseRole;
      sourceKey: string;
      contentModel: CustomAppContentModel;
    }>;
  }): string {
    const requestKey = input.requests
      .map(
        request =>
          `${request.sourceKey}:${request.role}:${request.contentModel}`
      )
      .sort((a, b) => a.localeCompare(b))
      .join('|');
    return `${input.workspaceId}:${input.customAppId}:${requestKey}`;
  }

  private assertServerOwnedDataSourcesPresent(
    customAppConfig: CustomAppConfig,
    requests: ReadonlyArray<{
      sourceKey: string;
    }>
  ): void {
    const dataSources = getCustomAppDataSources(customAppConfig);
    for (const request of requests) {
      if (dataSources[request.sourceKey]) continue;
      throw new Error(
        `ConfigProvisioner: server response did not include data source ${request.sourceKey}`
      );
    }
  }

  private isStandardDbBlockReady(model: unknown, expectedDatabaseId: string) {
    const props = (model as { props?: Record<string, unknown> }).props;
    if (props?.databaseId !== expectedDatabaseId) return false;
    try {
      return isDatabaseStorageModelReady(model as never);
    } catch {
      return false;
    }
  }

  private requireGraphQL(): GraphQLService {
    const graphql =
      this.workspaceServerService.server?.scope.get(GraphQLService) ?? null;
    if (!graphql) {
      throw new Error(
        'ConfigProvisioner: GraphQL service is not available for this workspace'
      );
    }
    return graphql;
  }

  private async waitForStandardDatabaseReady(
    bsDoc: Store,
    input:
      | string
      | {
          blockId: string;
          databaseId: string;
          workspaceId: string;
        },
    observeReadiness?: (state: {
      hostPresent: boolean;
      rootPresent: boolean;
      rootReady: boolean;
      storageReady: boolean;
      rowWindowReady: boolean | null;
      snapshotFullyLoaded: boolean | null;
    }) => void
  ): Promise<boolean> {
    const deadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
    let observedModel: object | null = null;
    let observedDataSource: ReturnType<
      typeof createDatabaseBlockDataSource
    > | null = null;
    try {
      while (Date.now() <= deadline) {
        const blockId = typeof input === 'string' ? input : input.blockId;
        const hostModel = bsDoc.getBlock(blockId)?.model;
        const rootDocId =
          typeof input === 'string'
            ? null
            : createServerDatabaseRootDocId({
                workspaceId: input.workspaceId,
                databaseId: input.databaseId,
              });
        const workspace = (
          bsDoc as Store & {
            workspace?: {
              createDoc?: (id: string) => {
                getStore?: (options: { id: string }) => Store;
                load?: () => void;
                ready?: boolean;
              };
              getDoc?: (id: string) => {
                getStore?: (options: { id: string }) => Store;
                load?: () => void;
                ready?: boolean;
              } | null;
              registerDoc?: (id: string) => unknown;
            };
          }
        ).workspace;
        let root = rootDocId ? (workspace?.getDoc?.(rootDocId) ?? null) : null;
        if (rootDocId && !root) {
          workspace?.registerDoc?.(rootDocId);
          root = workspace?.getDoc?.(rootDocId) ?? null;
        }
        if (rootDocId && !root) {
          root = workspace?.createDoc?.(rootDocId) ?? null;
        }
        if (root && !root.ready) root.load?.();
        const rootModel = rootDocId
          ? root
              ?.getStore?.({ id: rootDocId })
              ?.getBlock(typeof input === 'string' ? blockId : input.databaseId)
              ?.model
          : null;
        const model = hostModel ?? rootModel;
        const storageReady = Boolean(
          model && isDatabaseStorageModelReady(model as never)
        );
        let rowWindowReady: boolean | null = null;
        let snapshotFullyLoaded: boolean | null = null;
        if (model && storageReady) {
          if (observedModel !== model) {
            observedDataSource?.dispose();
            observedModel = model;
            observedDataSource = createDatabaseBlockDataSource(model as never);
          }
          // The readiness API owns cold row-shard registration/loading. Reading
          // the snapshot directly can otherwise poll an empty shell forever.
          // Keep one datasource for the whole bounded wait: empty-shard repair
          // deliberately confirms the same snapshot across multiple polls.
          const dataSource = observedDataSource;
          if (!dataSource) continue;
          rowWindowReady = dataSource.isInitialRowWindowReady();
          snapshotFullyLoaded = rowWindowReady
            ? isDatabaseSnapshotFullyLoaded(dataSource)
            : null;
        }
        observeReadiness?.({
          hostPresent: Boolean(hostModel),
          rootPresent: Boolean(rootModel),
          rootReady: root?.ready === true,
          storageReady,
          rowWindowReady,
          snapshotFullyLoaded,
        });
        if (rowWindowReady && snapshotFullyLoaded) return true;
        await sleep(STANDARD_DATABASE_READY_POLL_MS);
      }
      return false;
    } finally {
      observedDataSource?.dispose();
    }
  }

  private async waitForDataSourcesReady(
    customAppConfig: CustomAppConfig,
    sourceKeys: readonly string[],
    workspaceId: string,
    options: { retryPermissionLag?: boolean } = {}
  ): Promise<void> {
    const normalizedCustomAppConfig =
      normalizeCustomAppConfigDataSourceRefs(customAppConfig);
    const dataSources = getCustomAppDataSources(normalizedCustomAppConfig);
    const reconciledDocIds = new Set<string>();
    const reconciledStorageDocIds = new Set<string>();
    for (const sourceKey of new Set(sourceKeys)) {
      const ref = dataSources[sourceKey];
      if (!ref) continue;
      const { doc, release } = this.docsService.open(ref.docId);
      try {
        // Server-owned provisioning persists the host Y.Doc before returning,
        // but a browser worker may still hold the pre-mutation snapshot. Pull
        // the authoritative remote delta through the existing reconciliation
        // contract before judging the referenced database block as missing.
        if (!reconciledDocIds.has(ref.docId)) {
          const pullDeadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
          while (true) {
            try {
              await doc.workspace.engine.doc.pullRemote(ref.docId);
              break;
            } catch (error) {
              if (
                (!isCloudDocStorageConnectionNotReady(error) &&
                  (!options.retryPermissionLag ||
                    !isServerOwnedDataSourceAccessNotReady(error))) ||
                Date.now() >= pullDeadline
              ) {
                throw error;
              }
              // The server-owned mutation receipt is terminal, but an
              // already-connected sync worker can still finish its earlier
              // permission read before that commit becomes observable. Keep
              // the existing bounded readiness barrier on the same pull
              // contract; never surface this transient as an authoring error.
              await sleep(STANDARD_DATABASE_READY_POLL_MS);
            }
          }
          reconciledDocIds.add(ref.docId);
        }
        const storageDocId = createServerDatabaseRootDocId({
          workspaceId,
          databaseId: ref.databaseId,
        });
        if (!reconciledStorageDocIds.has(storageDocId)) {
          const pullDeadline = Date.now() + STANDARD_DATABASE_READY_TIMEOUT_MS;
          while (true) {
            try {
              await doc.workspace.engine.doc.pullRemote(storageDocId);
              break;
            } catch (error) {
              if (
                (!isCloudDocStorageConnectionNotReady(error) &&
                  (!options.retryPermissionLag ||
                    !isServerOwnedDataSourceAccessNotReady(error))) ||
                Date.now() >= pullDeadline
              ) {
                throw error;
              }
              await sleep(STANDARD_DATABASE_READY_POLL_MS);
            }
          }
          reconciledStorageDocIds.add(storageDocId);
        }
        const bsDoc = doc.blockSuiteDoc as Store | undefined;
        if (bsDoc) {
          bsDoc.load?.();
          await doc.waitForSyncReady();
        }
        if (!bsDoc?.root) {
          throw new Error(
            `ConfigProvisioner: doc ${ref.docId} is not ready (no root block)`
          );
        }
        let readiness = '';
        const ready = await this.waitForStandardDatabaseReady(
          bsDoc,
          {
            blockId: ref.blockId,
            databaseId: ref.databaseId,
            workspaceId,
          },
          state => {
            readiness = JSON.stringify(state);
          }
        );
        if (!ready) {
          throw new Error(
            `ConfigProvisioner: data source ${ref.key} storage is not ready` +
              (readiness ? ` (${readiness})` : '')
          );
        }
      } finally {
        release();
      }
    }
  }

  private ensureFixedViewConfigRow(input: {
    configDbBlockId: string;
    contentDbBlockId: string;
    dataSources: DataSourceRegistry;
    doc: unknown;
    docId: string;
    title: string;
    viewConfigRowId: string;
  }): void {
    const rowStore = this.buildRowStore(
      { doc: input.doc } as unknown as DocService,
      input.configDbBlockId
    );
    const expectedRow = createEmptyViewConfigRow({
      name: input.title,
      contentDbRef: {
        databaseId: input.contentDbBlockId,
        docId: input.docId,
        blockId: input.contentDbBlockId,
        contentModel: getInitialCustomAppContentModelForRole('content'),
      },
      dataSources: input.dataSources,
      isDefault: true,
    });
    const currentRow =
      rowStore.rows$.value.find(row => row.viewId === input.viewConfigRowId) ??
      null;

    if (!currentRow) {
      rowStore.createRow(expectedRow, input.viewConfigRowId);
      return;
    }

    const patch: Partial<ViewConfigRow> = {};
    if (!hasPublishedRootRoute(currentRow) && expectedRow.customAppConfig) {
      patch.customAppConfig = expectedRow.customAppConfig;
    }
    if (currentRow.contentDbRef == null) {
      patch.contentDbRef = expectedRow.contentDbRef;
    }
    if (!currentRow.isDefault) {
      patch.isDefault = true;
    }
    if (Object.keys(patch).length > 0) {
      rowStore.updateRow(input.viewConfigRowId, patch);
    }
  }

  private async ensureStandardDatabaseBlocks(
    bsDoc: Store,
    docId: string,
    blockIds: StandardDatabaseBlockIdMap,
    createdBlockIds: string[]
  ): Promise<StandardDatabaseBlockIdMap> {
    const normalizedBlockIds: StandardDatabaseBlockIdMap = {};
    for (const role of Object.keys(blockIds) as ProvisionedDatabaseRole[]) {
      const blockId = normalizeProvisionedDatabaseBlockId(
        blockIds[role],
        `standard database ${role}`
      );
      if (!blockId) {
        throw new Error(
          `ConfigProvisioner: missing standard database block id for ${role}`
        );
      }
      normalizedBlockIds[role] = blockId;
      const block = bsDoc.getBlock(blockId);
      if (!block?.model || !this.isStandardDbBlockReady(block.model, blockId)) {
        this.createStandardDb(bsDoc, role, createdBlockIds, blockId);
      }
      const ready = await this.waitForStandardDatabaseReady(bsDoc, blockId);
      if (!ready) {
        throw new Error(
          `ConfigProvisioner: standard database ${role} storage is not ready`
        );
      }
    }
    this.ensureProvisionedRelationColumns(
      bsDoc,
      this.buildDataSourceRegistry(docId, normalizedBlockIds)
    );
    return normalizedBlockIds;
  }

  /**
   * Compensating rollback for POST-provision failures (e.g. the
   * `createCustomApp` mutation fails after `provision()` has committed).
   * Reverses exactly the effects of `provision()`:
   *   - Deletes the ConfigDB.
   *   - If the ContentDB was freshly created, deletes it too. If the ContentDB
   *     was adopted, unsets the `sl_view_role` marker so the user's plain
   *     DatabaseBlock is returned to its original state.
   *
   * Best-effort: any step that throws is swallowed so the original caller
   * error stays surfaced. Callers should invoke this ONLY when they know the
   * post-provision step failed — calling twice is safe but wasteful.
   */
  async revert(
    docId: string,
    result: ProvisionResult,
    wasAdopted: boolean
  ): Promise<void> {
    try {
      const { doc, release } = this.docsService.open(docId);
      try {
        const bsDoc = doc.blockSuiteDoc as Store | undefined;
        if (!bsDoc) return;
        bsDoc.load?.();
        await doc.waitForSyncReady();

        const deleteBlockIds = new Set<string>([result.configDbBlockId]);
        for (const blockId of Object.values(
          result.standardDatabaseBlockIds ?? {}
        )) {
          if (blockId) deleteBlockIds.add(blockId);
        }

        if (wasAdopted) {
          deleteBlockIds.delete(result.contentDbBlockId);
          const adopted = bsDoc.getBlock(result.contentDbBlockId);
          const props = adopted?.model?.props as
            | Record<string, unknown>
            | undefined;
          if (props) {
            try {
              bsDoc.transact(() => {
                delete props[SL_VIEW_ROLE_KEY];
              });
            } catch {
              // best-effort
            }
          }
        } else {
          deleteBlockIds.add(result.contentDbBlockId);
        }

        for (const blockId of deleteBlockIds) {
          const block = bsDoc.getBlock(blockId);
          if (block?.model) {
            try {
              bsDoc.deleteBlock(block.model);
            } catch {
              // best-effort
            }
          }
        }
      } finally {
        release();
      }
    } catch {
      // best-effort
    }
  }

  /**
   * ViewConfigRowStore extends @toeverything/infra Store and must be built
   * inside a provider context. We don't need the DI lifecycle after the
   * store has seeded the initial row, so a throw-away Framework is enough.
   */
  private buildRowStore(
    docService: DocService,
    databaseBlockId: string
  ): ViewConfigRowStore {
    const framework = new Framework();
    framework.store(
      ViewConfigRowStore,
      () => new ViewConfigRowStore(docService, databaseBlockId)
    );
    return framework.provider().get(ViewConfigRowStore);
  }

  /**
   * Adoption path: the caller has already picked a DatabaseBlock. Validate
   * that it exists and is not already marked. Any `sl_view_role` value OR a
   * `managedBy: 'view-config'` tag blocks adoption — fail-loud.
   * (The outer per-doc ConfigDB guard in `provision()` catches most cases too;
   * this check is defense-in-depth for call sites that bypass the guard.)
   */
  private adoptContentDb(
    bsDoc: Store,
    blockId: string,
    adoptedMarks: Array<{ blockId: string; previousMark: string | null }>
  ): string {
    const block = bsDoc.getBlock(blockId);
    if (!block?.model) {
      throw new Error(
        `ConfigProvisioner: adoption target block ${blockId} not found`
      );
    }
    const props = (block.model.props ?? {}) as Record<string, unknown>;
    assertDatabaseIdentity({
      id: block.model.id,
      props,
    });
    const dataSource = createDatabaseBlockDataSource(block.model as any);
    const storageProps = (dataSource.storageModel as any).props as Record<
      string,
      unknown
    >;
    const manifest = normalizeDatabaseShardManifest(
      storageProps.databaseShardManifest
    );
    if (!manifest) {
      throw new Error(
        `ConfigProvisioner: adoption target ${blockId} is missing database shard manifest`
      );
    }
    const role = classifyDatabaseRole(key => props[key]);
    if (role !== 'adoptable') {
      throw new Error(
        `ConfigProvisioner: block ${blockId} is not adoptable (role=${role})`
      );
    }
    // Record the pre-adopt mark BEFORE the overwrite. `'adoptable'` covers
    // both "no mark" and managed tagged states — the only way to restore
    // correctly is to snapshot whatever value is actually on the block now.
    const rawPrevious = props[SL_VIEW_ROLE_KEY];
    const previousMark = typeof rawPrevious === 'string' ? rawPrevious : null;
    adoptedMarks.push({ blockId, previousMark });
    bsDoc.transact(() => {
      (block.model!.props as Record<string, unknown>)[SL_VIEW_ROLE_KEY] =
        SL_VIEW_ROLE_VALUES.CONTENT;
      (block.model!.props as Record<string, unknown>).managedBy =
        'custom-app-standard:content';
      (block.model!.props as Record<string, unknown>).schemaLocked = true;
    });
    return blockId;
  }

  private buildDataSourceRegistry(
    docId: string,
    blockIds: StandardDatabaseBlockIdMap,
    contentModelsByRole: Partial<
      Record<ProvisionedDatabaseRole, CustomAppContentModel>
    > = {}
  ): DataSourceRegistry {
    const registry: DataSourceRegistry = {};
    for (const role of Object.keys(blockIds) as ProvisionedDatabaseRole[]) {
      const blockId = normalizeProvisionedDatabaseBlockId(
        blockIds[role],
        `standard database ${role}`
      );
      if (!blockId) continue;
      registry[role] = createDefaultDataSourceRef({
        key: role,
        role,
        contentModel:
          contentModelsByRole[role] ??
          getDefaultCustomAppContentModelForRole(role),
        databaseId: blockId,
        docId,
        blockId,
        label: STANDARD_CUSTOM_APP_DATABASE_TITLES[role],
      });
    }
    return registry;
  }

  private ensureProvisionedRelationColumns(
    bsDoc: Store,
    dataSources: DataSourceRegistry
  ): void {
    const resolve = (role: CustomAppDataSourceRole, sourceKey?: string) => {
      const ref = dataSources[sourceKey ?? role];
      if (!ref) return null;
      const block = bsDoc.getBlock(ref.blockId);
      if (!block?.model) return null;
      return {
        dataSource: createDatabaseBlockDataSource(block.model as any),
      };
    };

    for (const role of STANDARD_CUSTOM_APP_DATABASE_ROLES) {
      ensureStandardRelationColumns({ dataSources }, role, resolve);
    }
  }

  /**
   * Standard-DB path: insert a Title-only `affine:database` block on the doc
   * root. The row content itself is user-managed — the provisioner only seeds
   * the DB shell and the database role marker.
   */
  private createStandardDb(
    bsDoc: Store,
    role: ProvisionedDatabaseRole,
    createdBlockIds: string[],
    databaseId?: string,
    contentModel?: CustomAppContentModel,
    label?: string
  ): string {
    const normalizedDatabaseId = normalizeProvisionedDatabaseBlockId(
      databaseId,
      `standard database ${role}`
    );
    const hadHostBlock =
      normalizedDatabaseId !== undefined &&
      Boolean(bsDoc.getBlock(normalizedDatabaseId)?.model);
    const layout = CUSTOM_APP_STANDARD_DATABASE_LAYOUTS[role];
    const title = label ?? STANDARD_CUSTOM_APP_DATABASE_TITLES[role];
    const initStandardStorageModel = (dataSource: any) => {
      const storageModel = dataSource.storageModel;
      storageModel.store.transact(() => {
        const columns = buildStandardColumns(role, contentModel);
        storageModel.props.schemaLocked = true;
        storageModel.props.managedBy = `custom-app-standard:${role}`;
        storageModel.props[SL_VIEW_ROLE_KEY] =
          SL_VIEW_ROLE_BY_STANDARD_ROLE[role];
        storageModel.props.columns = columns;
        storageModel.props.views = [
          {
            id: `view-${Math.random().toString(36).slice(2, 10)}`,
            name: 'Table View',
            mode: 'table',
            columns: [
              { id: 'title', hide: false, width: 260 },
              ...columns.map(column => ({
                id: column.id,
                hide: false,
                width: 180,
              })),
            ],
            filter: { type: 'group', op: 'and', conditions: [] },
            header: { titleColumn: 'title', iconColumn: 'type' },
          },
        ];

        const titleText = new YText();
        titleText.insert(0, title);
        storageModel.props.title = titleText;
      });
    };
    const provisioned = provisionShardedDatabaseBlockDataSource(bsDoc, {
      databaseId: normalizedDatabaseId,
      title,
      props: {
        xywh: serializeCustomAppDatabaseXYWH(layout),
        index: layout.index,
        lockedBySelf: false,
        schemaLocked: true,
        managedBy: `custom-app-standard:${role}`,
        [SL_VIEW_ROLE_KEY]: SL_VIEW_ROLE_BY_STANDARD_ROLE[role],
      },
      initDataSource: initStandardStorageModel,
    });
    const blockId = provisioned.databaseBlockId;
    if (!hadHostBlock) createdBlockIds.push(blockId);

    const dbModel = provisioned.model as {
      props: Record<string, unknown>;
      store?: { transact: (cb: () => void) => void };
    };
    bsDoc.updateBlock(dbModel as any, {
      schemaLocked: true,
      managedBy: `custom-app-standard:${role}`,
    });

    dbModel.store?.transact(() => {
      dbModel.props.schemaLocked = true;
      dbModel.props.managedBy = `custom-app-standard:${role}`;
      dbModel.props[SL_VIEW_ROLE_KEY] = SL_VIEW_ROLE_BY_STANDARD_ROLE[role];
    });

    return blockId;
  }
}
